import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, X, ChartLine, ShieldCheck, Sparkles, Sun, Moon, Lock, Eye } from "lucide-react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { BrandLogo, LOGO_URL } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import TestimonialsMarquee from "../components/TestimonialsMarquee";
import HowItWorks from "../components/HowItWorks";
import FAQ from "../components/FAQ";
import LiveDashboardMockup from "../components/LiveDashboardMockup";
import CaesarFeatures from "../components/CaesarFeatures";
import PricingSection from "../components/PricingSection";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const HERO_VARIANTS = ["landing_var_a", "landing_var_b", "landing_var_c", "landing_var_d"];
const HERO_ADJ = ["hero_adj_a", "hero_adj_b", "hero_adj_c", "hero_adj_d"];
const HERO_CYCLE_MS = 6000;

const Landing = () => {
    const { language, updateSettings, theme } = useApp();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    // Start at a random variant so first-time visitors don't always see the same opener.
    const [heroIdx, setHeroIdx] = useState(() => Math.floor(Math.random() * HERO_VARIANTS.length));
    const [shake, setShake] = useState(0); // increment to retrigger shake animation
    const pausedRef = useRef(false);

    const triggerShake = () => setShake((s) => s + 1);

    // If URL has ?auth_error, show a friendly error message like in the screenshot
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("auth_error")) {
            setError(true);
            // clean the URL
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    // Auto-cycle hero variants every HERO_CYCLE_MS. Pauses on hover so users can read.
    useEffect(() => {
        const id = setInterval(() => {
            if (pausedRef.current) return;
            setHeroIdx((i) => (i + 1) % HERO_VARIANTS.length);
        }, HERO_CYCLE_MS);
        return () => clearInterval(id);
    }, []);

    const handleGoogleLogin = () => {
        setLoading(true);
        setError(null);
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        // Native Google OAuth — client_id comes from REACT_APP_GOOGLE_CLIENT_ID so
        // rotating the OAuth client only takes an env update + redeploy, no code changes.
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        const redirectUri = window.location.origin + "/auth/google";
        const scope = encodeURIComponent("openid email profile");
        const state = Math.random().toString(36).slice(2);
        try { sessionStorage.setItem("vlg_oauth_state", state); } catch (_) {}
        window.location.href =
            `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}` +
            `&redirect_uri=${encodeURIComponent(redirectUri)}` +
            `&response_type=code&scope=${scope}&access_type=online&prompt=select_account&state=${state}`;
    };

    return (
        <div className="min-h-screen w-full" data-testid="landing-page">
            <div className="w-full flex flex-col md:flex-row min-h-screen">
            {/* LEFT: Cream editorial pane */}
            <div className="relative flex-1 min-h-screen bg-[hsl(var(--background))] px-5 sm:px-8 md:px-20 lg:px-24 flex flex-col gap-0 justify-between overflow-hidden">
                {/* top bar */}
                <div className="pt-6 md:pt-10 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 text-[hsl(var(--primary))]">
                        <BrandLogo size={30} />
                        <span className="font-display font-bold text-base md:text-lg tracking-tight text-[hsl(var(--foreground))]">Vive a lo Grande</span>
                    </div>
                    <div className="flex items-center gap-2 md:gap-4">
                        <a
                            href="#how-it-works"
                            data-testid="landing-how-it-works-link"
                            className="hidden sm:inline-block text-xs font-semibold tracking-[0.2em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors uppercase"
                        >
                            {t(language, "hiw_eyebrow")}
                        </a>
                        <a
                            href="#pricing"
                            data-testid="landing-pricing-link"
                            className="hidden sm:inline-block text-xs font-semibold tracking-[0.2em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors uppercase"
                        >
                            {t(language, "pricing_eyebrow")}
                        </a>
                        <button
                            data-testid="landing-theme-toggle"
                            onClick={() => updateSettings({ theme: theme === "dark" ? "light" : "dark" })}
                            aria-label="toggle theme"
                            className="w-9 h-9 grid place-items-center rounded-full border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]/40 transition-colors"
                        >
                            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                        <button
                            data-testid="landing-lang-toggle"
                            onClick={() => updateSettings({ language: language === "es" ? "en" : "es" })}
                            className="text-xs font-semibold tracking-[0.2em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors uppercase"
                        >
                            {language === "es" ? "EN" : "ES"}
                        </button>
                    </div>
                </div>

                {/* center */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.2, 0.9, 0.3, 1] }}
                    className="max-w-xl mx-auto my-auto text-center w-full"
                >
                    <div className="flex items-center justify-center gap-3 mb-6 md:mb-6">
                        <span className="h-px w-10 bg-[hsl(var(--muted-foreground))]/40" />
                        <span className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--muted-foreground))] uppercase">
                            {t(language, "eyebrow")}
                        </span>
                        <span className="h-px w-10 md:w-14 bg-[hsl(var(--muted-foreground))]/40" />
                    </div>

                    <h1
                        className="font-display font-extrabold text-[hsl(var(--primary))] leading-[1.05] tracking-[-0.03em] text-[clamp(2.5rem,11vw,4.5rem)] md:text-6xl lg:text-7xl break-words"
                        data-testid="hero-heading"
                    >
                        {t(language, "hero_prefix")}{" "}
                        <span className="relative inline-block align-baseline max-w-full">
                            {/* Reserve width from the widest adjective (+ suffix) so the layout doesn't jump when crossfading */}
                            <span aria-hidden className="invisible whitespace-nowrap">
                                {HERO_ADJ.map((k) => t(language, k)).sort((a, b) => b.length - a.length)[0]}
                                {t(language, "hero_suffix")}
                            </span>
                            <AnimatePresence mode="wait">
                                <motion.span
                                    key={HERO_ADJ[heroIdx] + language}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                    data-testid="hero-adjective"
                                    className="absolute left-0 top-0 text-[hsl(var(--accent))] whitespace-nowrap"
                                >
                                    {t(language, HERO_ADJ[heroIdx])}
                                    {t(language, "hero_suffix") ? (
                                        <span className="text-[hsl(var(--primary))]">{t(language, "hero_suffix")}</span>
                                    ) : null}
                                </motion.span>
                            </AnimatePresence>
                        </span>
                    </h1>

                    <div
                        onMouseEnter={() => { pausedRef.current = true; }}
                        onMouseLeave={() => { pausedRef.current = false; }}
                        className="mt-6 md:mt-8 max-w-xl mx-auto"
                        data-testid="landing-hero-copy-wrap"
                    >
                        <div className="relative min-h-[56px] md:min-h-[64px] flex items-center justify-center">
                            <AnimatePresence mode="wait">
                                <motion.p
                                    key={HERO_VARIANTS[heroIdx] + language}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                    data-testid="landing-hero-copy"
                                    className="absolute inset-0 flex items-center justify-center text-[hsl(var(--muted-foreground))] text-base md:text-lg leading-relaxed"
                                >
                                    {t(language, HERO_VARIANTS[heroIdx])}
                                </motion.p>
                            </AnimatePresence>
                        </div>

                        {/* Progress dots — click to jump to a variant */}
                        <div className="mt-4 flex items-center justify-center gap-2" role="tablist" aria-label="hero variants" data-testid="hero-dots">
                            {HERO_VARIANTS.map((v, i) => {
                                const active = i === heroIdx;
                                return (
                                    <button
                                        key={v}
                                        role="tab"
                                        aria-selected={active}
                                        aria-label={`hero variant ${i + 1}`}
                                        data-testid={`hero-dot-${i}`}
                                        onClick={() => setHeroIdx(i)}
                                        className={`h-1.5 rounded-full transition-all duration-500 ${active ? "w-8 bg-[hsl(var(--primary))]" : "w-2 bg-[hsl(var(--muted-foreground))]/30 hover:bg-[hsl(var(--muted-foreground))]/60"}`}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {error && (
                        <div
                            data-testid="auth-error-alert"
                            className="mt-8 flex items-start gap-3 rounded-2xl border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/10 px-4 py-3 max-w-md"
                        >
                            <AlertCircle className="w-4 h-4 mt-0.5 text-[hsl(var(--destructive))] shrink-0" />
                            <div className="flex-1 text-sm">
                                <div className="font-semibold text-[hsl(var(--foreground))]">
                                    {language === "es" ? "No se pudo iniciar sesión" : "Sign in failed"}
                                </div>
                                <div className="text-[hsl(var(--muted-foreground))] mt-0.5">
                                    {language === "es"
                                        ? "El servidor tuvo un problema momentáneo. Toca 'Continuar con Google' abajo para volver a intentarlo."
                                        : "The server had a momentary issue. Tap 'Continue with Google' below to try again."}
                                </div>
                            </div>
                            <button onClick={() => setError(null)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    <div className="mt-8">
                        {/* Trust badges — subtle cues right before the sign-in click */}
                        <div
                            className="mb-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]"
                            data-testid="landing-trust-badges"
                        >
                            <span className="inline-flex items-center gap-1.5" data-testid="trust-badge-plaid">
                                <ShieldCheck className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                                {t(language, "trust_plaid")}
                            </span>
                            <span className="inline-flex items-center gap-1.5" data-testid="trust-badge-encryption">
                                <Lock className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                                {t(language, "trust_encryption")}
                            </span>
                            <span className="inline-flex items-center gap-1.5" data-testid="trust-badge-readonly">
                                <Eye className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                                {t(language, "trust_readonly")}
                            </span>
                        </div>

                        <Button
                            data-testid="google-auth-button"
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            className="sunset-cta pill h-14 w-full sm:w-auto px-7 font-semibold text-base gap-3 mx-auto flex"
                        >
                            <span className="grid place-items-center w-6 h-6 rounded-full bg-[hsl(var(--accent))] text-white text-[10px] font-bold">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                    <path fill="currentColor" d="M21.35 11.1H12v2.94h5.35c-.23 1.24-.94 2.29-2 2.99v2.5h3.24c1.9-1.75 3-4.33 3-7.44 0-.66-.06-1.3-.16-1.99z"/>
                                    <path fill="currentColor" opacity="0.9" d="M12 22c2.7 0 4.97-.9 6.63-2.44l-3.24-2.5c-.9.6-2.04.96-3.39.96-2.61 0-4.83-1.76-5.62-4.13H3.02v2.6C4.67 19.95 8.06 22 12 22z"/>
                                </svg>
                            </span>
                            {loading ? t(language, "signing_in") : t(language, "continue_google")}
                        </Button>

                        <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
                            {t(language, "terms_line")}{" "}
                            <Link to="/terms" data-testid="link-terms" className="underline decoration-[hsl(var(--accent))] underline-offset-2 hover:text-[hsl(var(--foreground))]">
                                {t(language, "terms")}
                            </Link>{" "}
                            {t(language, "and")}{" "}
                            <Link to="/privacy" data-testid="link-privacy" className="underline decoration-[hsl(var(--accent))] underline-offset-2 hover:text-[hsl(var(--foreground))]">
                                {t(language, "privacy")}
                            </Link>
                            {" ↗"}
                        </div>
                        <div className="mt-5 text-[10px] tracking-[0.22em] text-[hsl(var(--muted-foreground))]/70 uppercase">
                            {t(language, "terms_footnote")}
                        </div>
                    </div>
                </motion.div>

                {/* bottom chips */}
                <div className="pt-4 pb-8 md:pb-10 flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-[hsl(var(--muted-foreground))]">
                    <div className="flex items-center gap-2"><ChartLine className="w-4 h-4" /> {t(language, "chip_analysis")}</div>
                    <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {t(language, "chip_secure")}</div>
                    <div className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> {t(language, "chip_ai")}</div>
                </div>
            </div>

            {/* RIGHT: Solid #0D1E2E space pane with globe + stars.
                On mobile, this is pushed below the fold so users first see the hero copy only.
                On tap/hover, the globe performs a playful shake. */}
            <div className="relative w-full md:flex-1 min-h-[80vh] md:min-h-screen overflow-hidden" style={{ background: "#0D1E2E" }}>
                {/* Soft warm glow behind the globe (kept, adjusted for dark bg) */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-[80%] max-w-[640px] aspect-square rounded-full opacity-70"
                         style={{
                             background: "radial-gradient(circle at 50% 55%, rgba(255,159,28,0.22) 0%, rgba(46,196,182,0.14) 42%, transparent 72%)",
                             filter: "blur(32px)",
                         }} />
                </div>

                {/* Stars & celestial elements overlaid on the dark space */}
                <svg
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="xMidYMid slice"
                    aria-hidden="true"
                >
                    <defs>
                        <radialGradient id="starGlow" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
                            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                        </radialGradient>
                    </defs>
                    {/* stars — small circles */}
                    {[
                        [8, 12, 0.35], [14, 22, 0.45], [22, 8, 0.4], [30, 18, 0.35], [38, 10, 0.35],
                        [46, 20, 0.3], [55, 6, 0.4], [62, 16, 0.35], [72, 8, 0.45], [82, 18, 0.35],
                        [90, 12, 0.4], [12, 34, 0.35], [22, 44, 0.4], [8, 52, 0.35], [16, 62, 0.4],
                        [10, 74, 0.35], [18, 84, 0.4], [26, 92, 0.35], [38, 88, 0.4], [50, 92, 0.35],
                        [60, 88, 0.4], [72, 92, 0.35], [82, 86, 0.4], [90, 78, 0.35], [92, 66, 0.4],
                        [88, 54, 0.35], [92, 42, 0.4], [86, 30, 0.35], [4, 42, 0.35], [6, 66, 0.4],
                    ].map(([cx, cy, r], i) => (
                        <circle key={i} cx={cx} cy={cy} r={r} fill="#FFFFFF" opacity={0.7 + (i % 3) * 0.1} />
                    ))}
                    {/* 4-point sparkles */}
                    {[
                        [18, 28, 1.2], [76, 30, 1], [82, 70, 1.1], [14, 68, 0.9], [50, 8, 1], [88, 88, 1.1],
                    ].map(([cx, cy, s], i) => (
                        <g key={`sp-${i}`} transform={`translate(${cx} ${cy})`} opacity="0.85">
                            <path
                                d={`M0 -${s} L${s * 0.3} 0 L0 ${s} L-${s * 0.3} 0 Z M -${s} 0 L 0 ${s * 0.3} L ${s} 0 L 0 -${s * 0.3} Z`}
                                fill="#FFF8E7"
                            />
                        </g>
                    ))}
                    {/* Sun / warm celestial orb (top-right corner) */}
                    <circle cx="90" cy="14" r="3.6" fill="url(#starGlow)" opacity="0.7" />
                    <circle cx="90" cy="14" r="1.6" fill="#F9C77E" opacity="0.95" />
                    {/* Moon / cool celestial orb (bottom-left) */}
                    <circle cx="7" cy="88" r="3" fill="url(#starGlow)" opacity="0.5" />
                    <circle cx="7" cy="88" r="1.3" fill="#DFF0E5" opacity="0.9" />
                    {/* Comet streak */}
                    <path d="M 5 12 Q 12 18 22 26" stroke="#FFFFFF" strokeWidth="0.28" fill="none" opacity="0.75" strokeLinecap="round" />
                    <circle cx="22" cy="26" r="0.7" fill="#FFFFFF" opacity="0.95" />
                    {/* Second comet */}
                    <path d="M 78 78 Q 84 74 92 70" stroke="#FFFFFF" strokeWidth="0.22" fill="none" opacity="0.55" strokeLinecap="round" />
                    <circle cx="92" cy="70" r="0.55" fill="#FFFFFF" opacity="0.85" />
                    {/* Faint orbital ring around center */}
                    <ellipse cx="50" cy="50" rx="34" ry="12" stroke="#FFFFFF" strokeWidth="0.18" fill="none" opacity="0.16" transform="rotate(-18 50 50)" />
                    <ellipse cx="50" cy="50" rx="40" ry="18" stroke="#FFFFFF" strokeWidth="0.12" fill="none" opacity="0.1" transform="rotate(22 50 50)" />
                </svg>

                {/* 3D floating globe */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.85, rotateX: 20 }}
                    animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ perspective: "1200px", transformStyle: "preserve-3d" }}
                    onMouseEnter={triggerShake}
                    onTouchStart={triggerShake}
                    data-testid="hero-globe-touch"
                >
                    <motion.div
                        key={`shake-${shake}`}
                        animate={shake > 0 ? {
                            x: [0, -14, 12, -10, 8, -6, 4, -2, 0],
                            rotate: [0, -3, 3, -2.5, 2, -1.5, 1, -0.5, 0],
                        } : {}}
                        transition={{ duration: 0.75, ease: "easeInOut" }}
                        className="relative w-[72%] max-w-[540px] aspect-square"
                        style={{ transformStyle: "preserve-3d" }}
                    >
                        <motion.div
                            animate={{
                                y: [0, -18, 0, 14, 0],
                                rotateY: [-6, 6, -6],
                                rotateZ: [-1.5, 1.5, -1.5],
                            }}
                            transition={{
                                duration: 9,
                                ease: "easeInOut",
                                repeat: Infinity,
                                times: [0, 0.3, 0.55, 0.8, 1],
                            }}
                            className="relative w-full h-full"
                            style={{ transformStyle: "preserve-3d" }}
                        >
                        {/* Ground shadow */}
                        <motion.div
                            animate={{
                                scale: [1, 0.85, 1, 0.9, 1],
                                opacity: [0.35, 0.2, 0.35, 0.25, 0.35],
                            }}
                            transition={{ duration: 9, ease: "easeInOut", repeat: Infinity }}
                            className="absolute left-1/2 -translate-x-1/2 bottom-[-8%] w-[72%] h-8 rounded-[50%]"
                            style={{ background: "radial-gradient(ellipse at center, rgba(15,23,40,0.35) 0%, rgba(15,23,40,0) 70%)", filter: "blur(12px)" }}
                        />

                        {/* Globe sphere with real image */}
                        <div
                            className="absolute inset-0 rounded-full overflow-hidden"
                            style={{
                                boxShadow:
                                    "0 40px 80px -20px rgba(15,23,40,0.28), 0 0 100px -10px rgba(255,159,28,0.35), inset -14px -22px 60px rgba(15,23,40,0.25), inset 18px 22px 60px rgba(255,255,255,0.55)",
                            }}
                        >
                            <img
                                src={LOGO_URL}
                                alt="Globe"
                                className="w-full h-full object-cover select-none pointer-events-none"
                                draggable={false}
                            />
                            <div
                                className="absolute inset-0 rounded-full"
                                style={{
                                    background:
                                        "radial-gradient(circle at 32% 26%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.1) 22%, rgba(255,255,255,0) 45%)",
                                    mixBlendMode: "screen",
                                }}
                            />
                            <div
                                className="absolute inset-0 rounded-full"
                                style={{
                                    background:
                                        "radial-gradient(circle at 78% 82%, rgba(15,23,40,0.4) 0%, rgba(15,23,40,0.15) 25%, rgba(15,23,40,0) 55%)",
                                    mixBlendMode: "multiply",
                                }}
                            />
                            <div className="absolute inset-0 rounded-full grain opacity-30" />
                            <div
                                className="absolute inset-0 rounded-full"
                                style={{
                                    background:
                                        "radial-gradient(ellipse 55% 30% at 40% 12%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 70%)",
                                    mixBlendMode: "overlay",
                                }}
                            />
                        </div>

                        {/* Outer atmosphere ring */}
                        <div
                            className="absolute -inset-3 rounded-full pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(circle, rgba(255,255,255,0) 62%, rgba(46,196,182,0.28) 66%, rgba(255,255,255,0) 74%)",
                            }}
                        />
                        </motion.div>
                    </motion.div>
                </motion.div>

                {/* Live product mockup — floats top-right, animated hint of the real app */}
                <LiveDashboardMockup />

                {/* Floating ESTE MES metric card (glass tinted for dark bg) */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="absolute left-4 right-4 md:right-auto md:left-12 bottom-6 md:bottom-14 md:max-w-[380px] rounded-3xl p-5 md:p-6 shadow-2xl"
                    style={{
                        background: "rgba(255,255,255,0.08)",
                        backdropFilter: "blur(18px) saturate(140%)",
                        WebkitBackdropFilter: "blur(18px) saturate(140%)",
                        border: "1px solid rgba(255,255,255,0.14)",
                    }}
                    data-testid="hero-metric-card"
                >
                    <div className="text-[10px] font-bold tracking-[0.28em] text-[hsl(var(--primary))] uppercase">
                        {t(language, "this_month")}
                    </div>
                    <div className="mt-2 font-display font-bold text-white text-4xl md:text-5xl tracking-tight">
                        $4,268<span className="text-white/50">.50</span>
                    </div>
                    <div className="mt-3 text-sm text-white/70">
                        {t(language, "net_savings_ok")}
                    </div>
                </motion.div>
            </div>
            </div>

            {/* What Caesar does for you — 4 benefit-led features */}
            <CaesarFeatures />

            {/* How it works — 4-step editorial explainer */}
            <HowItWorks />

            {/* Pricing & Tiers — Explorer / Pro / Elite */}
            <PricingSection />

            {/* FAQ — collapsible answers to the top 5 questions */}
            <FAQ />

            {/* Testimonials — social proof rail below the hero */}
            <TestimonialsMarquee />

            {/* Footer strip — Blog, legal & contact discovery */}
            <footer className="border-t border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-6 text-sm text-[hsl(var(--muted-foreground))]">
                    <div className="flex items-center gap-2 text-[hsl(var(--foreground))]/80">
                        <span className="font-display font-bold">Vive a lo Grande</span>
                        <span className="opacity-40">·</span>
                        <span className="text-xs">{language === "es" ? "Finanzas, sin misterio." : "Finance, demystified."}</span>
                    </div>
                    <div className="flex items-center gap-6">
                        <Link
                            to="/blog"
                            data-testid="footer-blog-link"
                            className="hover:text-[hsl(var(--primary))] transition-colors"
                        >
                            {language === "es" ? "Blog" : "Blog"}
                        </Link>
                        <Link to="/terms" className="hover:text-[hsl(var(--foreground))]">
                            {t(language, "terms")}
                        </Link>
                        <Link to="/privacy" className="hover:text-[hsl(var(--foreground))]">
                            {t(language, "privacy")}
                        </Link>
                        <a href="mailto:vivealograndeus@gmail.com" className="hover:text-[hsl(var(--foreground))]">
                            {language === "es" ? "Contacto" : "Contact"}
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
